import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FiCheck, FiX, FiDownload, FiAlertCircle, FiTag } from 'react-icons/fi';
import publicService from '../../../../services/public.service';
import { API_URL } from '../../../../services/api';

const PublicQuotationReview = () => {
    const { uuid } = useParams();
    const [quotation, setQuotation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        loadQuotation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uuid]);

    const loadQuotation = async () => {
        try {
            setLoading(true);
            const data = await publicService.getQuotationByUuid(uuid);
            setQuotation(data);
        } catch (err) {
            console.error('Error loading quotation:', err);
            setError('No pudimos encontrar la cotización o el enlace ha expirado.');
        } finally {
            setLoading(false);
        }
    };

    const handleAccept = async () => {
        if (!window.confirm('¿Estás seguro de que deseas aceptar esta cotización?')) return;

        try {
            setActionLoading(true);
            await publicService.acceptQuotation(uuid);
            setSuccessMessage('¡Cotización aceptada exitosamente! Nos pondremos en contacto contigo.');
            loadQuotation();
        } catch (err) {
            console.error('Error accepting:', err);
            alert('Error al aceptar la cotización. Intenta nuevamente.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleReject = async () => {
        if (!window.confirm('¿Estás seguro de que deseas rechazar esta cotización?')) return;
        const motivo = window.prompt("Indica el motivo del rechazo (Opcional):");

        try {
            setActionLoading(true);
            await publicService.rejectQuotation(uuid, motivo);
            setSuccessMessage('Has rechazado la cotización.');
            loadQuotation();
        } catch (err) {
            console.error('Error rejecting:', err);
            alert('Error al rechazar la cotización. Intenta nuevamente.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDownload = async () => {
        const downloadUrl = `${API_URL}/cotizaciones/download_public/?uuid=${uuid}`;
        try {
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', `Cotizacion_${quotation.numero}.pdf`);
            link.setAttribute('target', '_blank');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error('Download error:', err);
            alert('Error al iniciar la descarga.');
        }
    };

    const formatCurrency = (val) => {
        return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(val);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('es-CL');
    };

    // --- Styles ---
    const styles = {
        pageWrapper: {
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #fdf2f8 100%)',
            padding: '32px 16px',
            fontFamily: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
        },
        container: {
            maxWidth: '800px',
            margin: '0 auto',
        },
        // Header Card
        headerCard: {
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
            borderRadius: '20px 20px 0 0',
            padding: '40px 32px 32px',
            textAlign: 'center',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
        },
        headerDecor: {
            position: 'absolute',
            top: '-60px',
            right: '-60px',
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.08)',
        },
        headerDecor2: {
            position: 'absolute',
            bottom: '-40px',
            left: '-40px',
            width: '150px',
            height: '150px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.05)',
        },
        headerLogo: {
            maxHeight: '70px',
            maxWidth: '200px',
            objectFit: 'contain',
            marginBottom: '16px',
            filter: 'brightness(0) invert(1)',
        },
        headerLogoAlt: {
            maxHeight: '70px',
            maxWidth: '200px',
            objectFit: 'contain',
            marginBottom: '16px',
        },
        headerTitle: {
            fontSize: '28px',
            fontWeight: '700',
            margin: '0 0 8px 0',
            letterSpacing: '-0.5px',
            position: 'relative',
        },
        headerSubtitle: {
            fontSize: '14px',
            opacity: '0.85',
            margin: '0',
            fontWeight: '400',
        },
        // Main Content
        contentCard: {
            backgroundColor: '#ffffff',
            borderRadius: '0 0 20px 20px',
            padding: '32px',
            boxShadow: '0 8px 32px rgba(99, 102, 241, 0.1)',
        },
        // Status Badges
        statusBadge: (type) => ({
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 20px',
            borderRadius: '50px',
            fontSize: '14px',
            fontWeight: '600',
            ...(type === 'ACEPTADA' ? {
                backgroundColor: '#dcfce7',
                color: '#166534',
                border: '1px solid #86efac',
            } : type === 'RECHAZADA' ? {
                backgroundColor: '#fef2f2',
                color: '#991b1b',
                border: '1px solid #fca5a5',
            } : {
                backgroundColor: '#eef2ff',
                color: '#4338ca',
                border: '1px solid #a5b4fc',
            }),
        }),
        // Success Message
        successCard: (isAccepted) => ({
            background: isAccepted
                ? 'linear-gradient(135deg, #dcfce7 0%, #d1fae5 100%)'
                : 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
            borderRadius: '16px',
            padding: '28px',
            marginBottom: '24px',
            textAlign: 'center',
            border: `1px solid ${isAccepted ? '#86efac' : '#fca5a5'}`,
        }),
        // Table Section
        sectionHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingBottom: '16px',
            marginBottom: '20px',
            borderBottom: '2px solid #eef2ff',
        },
        sectionTitle: {
            fontSize: '18px',
            fontWeight: '700',
            color: '#1e293b',
            margin: 0,
        },
        downloadBtn: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 20px',
            borderRadius: '10px',
            border: '1px solid #e2e8f0',
            backgroundColor: '#f8fafc',
            color: '#475569',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '600',
            transition: 'all 0.2s ease',
        },
        // Table
        table: {
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: '0',
        },
        tableHeader: {
            background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)',
            color: '#64748b',
            fontSize: '12px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontWeight: '600',
        },
        thCell: {
            padding: '12px 14px',
            textAlign: 'left',
        },
        tdCell: {
            padding: '14px',
            borderBottom: '1px solid #f1f5f9',
            fontSize: '14px',
            color: '#334155',
        },
        // Discount Badge
        discountBadge: {
            display: 'inline-block',
            background: 'linear-gradient(135deg, #10b981, #059669)',
            color: 'white',
            padding: '2px 10px',
            borderRadius: '6px',
            fontSize: '11px',
            fontWeight: '700',
            marginTop: '4px',
        },
        // Totals
        totalRow: {
            fontWeight: '600',
            color: '#334155',
        },
        grandTotalRow: {
            fontSize: '20px',
            fontWeight: '800',
            background: 'linear-gradient(135deg, #eef2ff 0%, #fdf2f8 100%)',
        },
        grandTotalLabel: {
            padding: '18px 14px',
            textAlign: 'right',
            color: '#6366f1',
        },
        grandTotalValue: {
            padding: '18px 14px',
            textAlign: 'right',
            color: '#6366f1',
        },
        // Action Buttons
        actionsCard: {
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            padding: '32px',
            marginTop: '20px',
            textAlign: 'center',
            boxShadow: '0 4px 16px rgba(99, 102, 241, 0.06)',
            border: '1px solid #eef2ff',
        },
        acceptBtn: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '14px 32px',
            borderRadius: '12px',
            border: 'none',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            cursor: 'pointer',
            fontSize: '15px',
            fontWeight: '700',
            flex: 1,
            maxWidth: '220px',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
            transition: 'all 0.2s ease',
        },
        rejectBtn: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '14px 32px',
            borderRadius: '12px',
            border: '2px solid #fca5a5',
            backgroundColor: '#fff',
            color: '#dc2626',
            cursor: 'pointer',
            fontSize: '15px',
            fontWeight: '700',
            flex: 1,
            maxWidth: '220px',
            transition: 'all 0.2s ease',
        },
        // Offer Card
        offerCard: {
            background: 'linear-gradient(135deg, #eef2ff 0%, #fdf2f8 100%)',
            border: '2px solid #a5b4fc',
            borderRadius: '14px',
            padding: '20px',
            marginBottom: '24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px',
        },
        offerSpecialCard: {
            background: 'linear-gradient(135deg, #dcfce7 0%, #d1fae5 100%)',
            border: '2px solid #86efac',
            borderRadius: '14px',
            padding: '24px',
            marginBottom: '24px',
            textAlign: 'center',
        },
        // Footer
        footer: {
            textAlign: 'center',
            marginTop: '40px',
            paddingBottom: '16px',
        },
        footerText: {
            fontSize: '12px',
            background: 'linear-gradient(135deg, #6366f1, #ec4899)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: '600',
            letterSpacing: '0.5px',
        },
        footerSub: {
            fontSize: '11px',
            color: '#94a3b8',
            marginTop: '4px',
        },
    };

    // --- Loading State ---
    if (loading) {
        return (
            <div style={{
                ...styles.pageWrapper,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{
                        width: '50px',
                        height: '50px',
                        border: '4px solid #eef2ff',
                        borderTopColor: '#6366f1',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite',
                        margin: '0 auto 16px',
                    }} />
                    <p style={{ color: '#6366f1', fontWeight: '600' }}>Cargando cotización...</p>
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </div>
            </div>
        );
    }

    // --- Error State ---
    if (error) {
        return (
            <div style={{
                ...styles.pageWrapper,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                <div style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '20px',
                    padding: '48px 40px',
                    textAlign: 'center',
                    maxWidth: '440px',
                    boxShadow: '0 8px 32px rgba(99, 102, 241, 0.1)',
                }}>
                    <div style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #fef2f2, #fee2e2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 20px',
                    }}>
                        <FiAlertCircle size={32} color="#ef4444" />
                    </div>
                    <h3 style={{ color: '#1e293b', marginBottom: '12px', fontSize: '20px' }}>Enlace Inválido</h3>
                    <p style={{ color: '#64748b', lineHeight: '1.6' }}>{error}</p>
                </div>
            </div>
        );
    }

    if (!quotation) return null;

    const isFinished = ['ACEPTADA', 'RECHAZADA'].includes(quotation.estado);
    const hasProductDiscounts = quotation.detalles && quotation.detalles.some(d => d.descuento_producto && d.descuento_producto > 0);
    const hasAnyOffer = quotation.oferta_activa || hasProductDiscounts;
    const empresa = quotation.empresa_info || {};

    return (
        <div style={styles.pageWrapper}>
            <div style={styles.container}>

                {/* ===== GRADIENT HEADER ===== */}
                <div style={styles.headerCard}>
                    <div style={styles.headerDecor} />
                    <div style={styles.headerDecor2} />
                    {empresa.logo_url && (
                        <div style={{ marginBottom: '16px', position: 'relative' }}>
                            <img
                                src={empresa.logo_url}
                                alt={empresa.nombre}
                                style={styles.headerLogoAlt}
                            />
                        </div>
                    )}
                    <h1 style={styles.headerTitle}>Cotización #{quotation.numero}</h1>
                    <p style={styles.headerSubtitle}>
                        Fecha: {formatDate(quotation.fecha_creacion)}
                        {quotation.fecha_vencimiento && ` · Válida hasta: ${formatDate(quotation.fecha_vencimiento)}`}
                    </p>
                </div>

                {/* ===== CONTENT CARD ===== */}
                <div style={styles.contentCard}>

                    {/* Success Message */}
                    {successMessage && (
                        <div style={styles.successCard(quotation.estado === 'ACEPTADA')}>
                            <div style={{ fontSize: '32px', marginBottom: '12px' }}>
                                {quotation.estado === 'ACEPTADA' ? '✅' : '❌'}
                            </div>
                            <h3 style={{
                                margin: '0 0 8px 0',
                                color: quotation.estado === 'ACEPTADA' ? '#166534' : '#991b1b',
                                fontSize: '18px',
                            }}>{successMessage}</h3>
                        </div>
                    )}

                    {!successMessage && (
                        <>
                            {/* Status: Finished */}
                            {isFinished && !hasAnyOffer && (
                                <div style={{ textAlign: 'center', marginBottom: '28px' }}>
                                    <span style={styles.statusBadge(quotation.estado)}>
                                        {quotation.estado === 'ACEPTADA' ? <FiCheck size={16} /> : <FiX size={16} />}
                                        Esta cotización ya ha sido {quotation.estado.toLowerCase()}.
                                    </span>
                                </div>
                            )}

                            {/* Special Offer Banner for Rejected */}
                            {hasProductDiscounts && quotation.estado === 'RECHAZADA' && (
                                <div style={styles.offerSpecialCard}>
                                    <h3 style={{ margin: '0 0 10px 0', color: '#166534', fontSize: '18px' }}>
                                        ¡Nueva Oferta Especial Disponible!
                                    </h3>
                                    <p style={{ margin: 0, color: '#047857', fontSize: '15px', lineHeight: '1.5' }}>
                                        Hemos aplicado descuentos especiales a los productos de esta cotización.
                                        ¡Esta es una oportunidad limitada que no puedes dejar pasar!
                                    </p>
                                </div>
                            )}

                            {/* Offer Active Banner */}
                            {quotation.es_oferta && (
                                <div style={styles.offerCard}>
                                    <div>
                                        <h4 style={{ margin: 0, color: '#4338ca', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <FiTag /> ¡Oferta Especial Aplicada!
                                        </h4>
                                        <p style={{ margin: '5px 0 0', color: '#6366f1', fontSize: '14px' }}>
                                            Esta cotización incluye un descuento exclusivo de <strong>{quotation.oferta_tipo_descuento === 'PORCENTAJE' ? `${parseFloat(quotation.oferta_valor_descuento)}%` : formatCurrency(quotation.oferta_valor_descuento)}</strong>
                                            {hasProductDiscounts ? ' y descuentos exclusivos en algunos productos.' : '.'}
                                        </p>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <span style={{ fontSize: '12px', color: '#6366f1', display: 'block' }}>Válida hasta:</span>
                                        <strong style={{ color: '#1e293b' }}>{quotation.oferta_vence ? new Date(quotation.oferta_vence).toLocaleDateString('es-CL') : 'N/A'}</strong>
                                    </div>
                                </div>
                            )}

                            {/* ===== PRODUCTS TABLE ===== */}
                            <div style={styles.sectionHeader}>
                                <h3 style={styles.sectionTitle}>Detalle de Productos</h3>
                                <button style={styles.downloadBtn} onClick={handleDownload}>
                                    <FiDownload size={16} /> Descargar PDF
                                </button>
                            </div>

                            <div style={{ overflowX: 'auto' }}>
                                <table style={styles.table}>
                                    <thead>
                                        <tr style={styles.tableHeader}>
                                            <th style={styles.thCell}>Producto</th>
                                            <th style={{ ...styles.thCell, textAlign: 'center' }}>Cant.</th>
                                            <th style={{ ...styles.thCell, textAlign: 'right' }}>Precio Unit.</th>
                                            <th style={{ ...styles.thCell, textAlign: 'right' }}>Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {quotation.detalles && quotation.detalles.map((detalle, index) => {
                                            const hasProductDiscount = detalle.descuento_producto && parseFloat(detalle.descuento_producto) > 0;
                                            
                                            const originalPrice = parseFloat(detalle.precio_unitario);
                                            let discountedPrice = originalPrice;
                                            
                                            if (hasProductDiscount) {
                                                if (detalle.descuento_tipo === 'MONTO') {
                                                    discountedPrice = (originalPrice * detalle.cantidad - parseFloat(detalle.descuento_producto)) / detalle.cantidad;
                                                } else {
                                                    discountedPrice = originalPrice * (1 - parseFloat(detalle.descuento_producto) / 100);
                                                }
                                            }

                                            return (
                                                <tr key={index} style={{
                                                    backgroundColor: hasProductDiscount ? '#f0fdf4' : 'transparent',
                                                }}>
                                                    <td style={styles.tdCell}>
                                                        <div style={{ fontWeight: '600', color: '#1e293b' }}>
                                                            {detalle.producto_nombre || detalle.producto}
                                                        </div>
                                                        {hasProductDiscount && (
                                                            <span style={styles.discountBadge}>
                                                                {detalle.descuento_tipo === 'MONTO' 
                                                                    ? `${formatCurrency(detalle.descuento_producto)} Total` 
                                                                    : `${parseFloat(detalle.descuento_producto)}%`} OFF
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td style={{ ...styles.tdCell, textAlign: 'center' }}>{detalle.cantidad}</td>
                                                    <td style={{ ...styles.tdCell, textAlign: 'right' }}>
                                                        {hasProductDiscount ? (
                                                            <div>
                                                                <div style={{ fontSize: '12px', color: '#9ca3af', textDecoration: 'line-through' }}>
                                                                    {formatCurrency(originalPrice)}
                                                                </div>
                                                                <div style={{ fontWeight: '700', color: '#10b981' }}>
                                                                    {formatCurrency(discountedPrice)}
                                                                </div>
                                                            </div>
                                                        ) : formatCurrency(originalPrice)}
                                                    </td>
                                                    <td style={{ ...styles.tdCell, textAlign: 'right', fontWeight: '600' }}>
                                                        {formatCurrency(detalle.total)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan="3" style={{ ...styles.tdCell, textAlign: 'right', ...styles.totalRow }}>Subtotal (Neto):</td>
                                            <td style={{ ...styles.tdCell, textAlign: 'right', ...styles.totalRow }}>{formatCurrency(quotation.subtotal)}</td>
                                        </tr>
                                        <tr>
                                            <td colSpan="3" style={{ ...styles.tdCell, textAlign: 'right', ...styles.totalRow }}>Impuestos:</td>
                                            <td style={{ ...styles.tdCell, textAlign: 'right', ...styles.totalRow }}>{formatCurrency(quotation.impuesto)}</td>
                                        </tr>
                                        
                                        {quotation.descuento_global_monto > 0 && (
                                            <tr style={{ background: 'linear-gradient(135deg, #eef2ff, #fdf2f8)' }}>
                                                <td colSpan="3" style={{ 
                                                    padding: '12px 14px', textAlign: 'right', 
                                                    fontWeight: '700', color: '#6366f1' 
                                                }}>
                                                    Descuento de la Oferta ({quotation.oferta_tipo_descuento === 'PORCENTAJE' ? `${parseFloat(quotation.oferta_valor_descuento)}%` : 'Monto Fijo'}):
                                                </td>
                                                <td style={{ 
                                                    padding: '12px 14px', textAlign: 'right', 
                                                    fontWeight: '700', color: '#6366f1' 
                                                }}>
                                                    - {formatCurrency(quotation.descuento_global_monto)}
                                                </td>
                                            </tr>
                                        )}

                                        <tr style={styles.grandTotalRow}>
                                            <td colSpan="3" style={styles.grandTotalLabel}>Total:</td>
                                            <td style={styles.grandTotalValue}>{formatCurrency(quotation.total)}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </>
                    )}
                </div>

                {/* ===== ACTION BUTTONS ===== */}
                {(!isFinished || hasAnyOffer) && !successMessage && (
                    <div style={styles.actionsCard}>
                        <h3 style={{ marginBottom: '8px', color: '#1e293b', fontSize: '18px' }}>
                            {quotation.oferta_activa ? '¡Oferta Especial Disponible!' : '¿Qué deseas hacer?'}
                        </h3>
                        <p style={{ color: '#64748b', marginBottom: '24px', fontSize: '14px' }}>
                            Selecciona una opción para continuar con tu cotización.
                        </p>
                        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button
                                style={styles.rejectBtn}
                                onClick={handleReject}
                                disabled={actionLoading}
                            >
                                <FiX size={18} /> {quotation.estado === 'RECHAZADA' ? 'Rechazar Oferta' : 'Rechazar'}
                            </button>
                            <button
                                style={styles.acceptBtn}
                                onClick={handleAccept}
                                disabled={actionLoading}
                            >
                                <FiCheck size={18} /> {quotation.oferta_activa ? 'Aceptar Oferta' : 'Aceptar Cotización'}
                            </button>
                        </div>
                    </div>
                )}

                {/* ===== FOOTER ===== */}
                <div style={styles.footer}>
                    <p style={styles.footerText}>⚡ Powered by IdevPlus</p>
                    <p style={styles.footerSub}>Soluciones tecnológicas para tu empresa</p>
                </div>

            </div>
        </div>
    );
};

export default PublicQuotationReview;
